import {useRouting} from '@/composables/use-routing.js'

/**
 * Navigation helpers for moving across the app
 */
export function useNavigation() {

  //Routing
  const {pushRoute, withParams, withQuery} = useRouting()

  //Home
  const goToHome = (...args) => pushRoute('home', ...args)

  //Enrollments
  const goToEnroll = (...args) => pushRoute('enroll', ...args)
  const goToManageEnrollment = (enrollmentId, ...args) => pushRoute(
    withParams('manage-enrollment', {enrollmentId}), ...args
  )

  //Conversation views for channel/enrollment
  const goToConversationForChannelId = (channelId, query = {}, ...args) => pushRoute(
    withQuery(withParams('conversation.channel', {channelId}), query),
    ...args
  )
  const goToConversationForEnrollmentId = (enrollmentId, query = {}, ...args) => pushRoute(
    withQuery(withParams('conversation.enrollment', {enrollmentId}), query),
    ...args
  )

  //Helper to navigate to conversation view for an enrollment model
  const goToConversationForEnrollment = (enrollment, options = {}) => {
    if (enrollment.hasChannel) {
      const query = {}
      if (options.scrollToFirstMessage) {
        query.enrollmentId = enrollment.id
      }
      goToConversationForChannelId(enrollment.channel.id, query)
    }
    else {
      goToConversationForEnrollmentId(enrollment.id)
    }
  }

  //Agent conversation (employee-facing name "Arist Agent")
  const goToAgent = (...args) => pushRoute('agent', ...args)

  //Extract deep link data from a deep link string
  const extractDeepLinkData = (deepLink) => {
    const raw = String(deepLink || '')
    const match = raw.match(/^([^|]+)(?:.*\bmessageId=([^|]+))?/)
    return {
      path: match?.[1] ?? '',
      messageId: match?.[2] ?? null,
    }
  }

  //Employee portal
  const goToEmployeeLogin = (...args) => pushRoute('employee-login', ...args)
  const goToEmployeeOnboarding = (...args) => pushRoute('employee-onboarding', ...args)

  //Follow deep link provided by Teams
  const followTeamsDeepLink = deepLink => {
    const {path, messageId} = extractDeepLinkData(deepLink)

    //Enrollment management (backwards compatibility)
    if (path === 'manage-enrollments' || path === 'enrollments-overview' || path === 'home') {
      goToHome()
    }

    //Differentiate between enrollment and channel deep links
    else if (path.startsWith('channel/')) {
      const channelId = path.split('/')[1]
      goToConversationForChannelId(channelId, {messageId})
    }
    else if (path.startsWith('enrollment/')) {
      const enrollmentId = path.split('/')[1]
      goToConversationForEnrollmentId(enrollmentId, {messageId})
    }

    //Fall back to enrollment view (v2)
    else {
      goToConversationForEnrollmentId(path, {messageId})
    }
  }

  //Expose interface
  return {

    //Home
    goToHome,

    //Enrollments
    goToEnroll,
    goToManageEnrollment,

    //Conversation views
    goToConversationForEnrollment,

    //Agent
    goToAgent,

    //Employee portal
    goToEmployeeLogin,
    goToEmployeeOnboarding,

    //Teams deep link helper
    followTeamsDeepLink,
  }
}
